import React, { useState, useEffect, useRef } from "react";
import Header from "../components/common/header";
import SelectCoins from "../components/Compare/SelectCoins";
import SelectDays from "../components/common/SelectDays";
import ToggleComponent from "../components/common/ToggleButton";
import LineChart from "../components/coin/LineChart";
import { getCoinPrices } from "../functions/getCoinPrices";
import { convertCoinObject } from "../functions/convertCoinObject";
import { getCoinData } from "../functions/getCoinData";
import { convertDate } from "../functions/convertDate";
import Loader from "../components/common/Loader";
import ListComponent from "../components/Dashboard/ListComponent";
import CoinInfo from "../components/coin/coinInfo";

export default function ComparePage() {
  const [crypto1, setCrypto1] = useState("bitcoin");
  const [crypto2, setCrypto2] = useState("ethereum");
  const [days, setDays] = useState(30);
  const [priceType, setPriceType] = useState("current_price");
  const [crypto1Data, setCrypto1Data] = useState({});
  const [crypto2Data, setCrypto2Data] = useState({});
  const [dataSet, setDataSet] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  /** Last coin ids we successfully loaded details for (updated only after fetch). */
  const prevCoinsRef = useRef({ c1: null, c2: null });
  const chartNamesRef = useRef({ n1: "", n2: "" });

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    const prev = prevCoinsRef.current;
    const need1 = prev.c1 !== crypto1;
    const need2 = prev.c2 !== crypto2;

    (async () => {
      try {
        if (need1) {
          const d1 = await getCoinData(crypto1);
          if (cancelled || !d1) return;
          convertCoinObject(setCrypto1Data, d1);
          chartNamesRef.current.n1 = d1.name ?? "";
          prevCoinsRef.current = {
            ...prevCoinsRef.current,
            c1: crypto1,
          };
        }
        if (need2) {
          const d2 = await getCoinData(crypto2);
          if (cancelled || !d2) return;
          convertCoinObject(setCrypto2Data, d2);
          chartNamesRef.current.n2 = d2.name ?? "";
          prevCoinsRef.current = {
            ...prevCoinsRef.current,
            c2: crypto2,
          };
        }

        const name1 = chartNamesRef.current.n1;
        const name2 = chartNamesRef.current.n2;
        if (!name1 || !name2) return;

        const [pricesData1, pricesData2] = await Promise.all([
          getCoinPrices(crypto1, days, priceType),
          getCoinPrices(crypto2, days, priceType),
        ]);
        if (cancelled) return;

        if (
          Array.isArray(pricesData1) &&
          Array.isArray(pricesData2) &&
          pricesData1.length > 0 &&
          pricesData2.length > 0
        ) {
          setDataSet({
            labels: pricesData1.map((item) => convertDate(item[0])),
            datasets: [
              {
                label: `${name1} Price in USD`,
                data: pricesData1.map((item) => item[1]),
                tension: 0.25,
                borderColor: "#8a3ffc",
                pointRadius: 0,
              },
              {
                label: `${name2} Price in USD`,
                data: pricesData2.map((item) => item[1]),
                tension: 0.25,
                borderColor: "#3a80e9",
                pointRadius: 0,
              },
            ],
          });
        } else {
          setDataSet({});
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [crypto1, crypto2, days, priceType]);

  return (
    <div>
      <Header />
      {isLoading ? (
        <Loader />
      ) : (
        <div>
          <div className="select-flex">
            <SelectCoins
              crypto1={crypto1}
              crypto2={crypto2}
              setCrypto1={setCrypto1}
              setCrypto2={setCrypto2}
            />
            <SelectDays className="selectDays" days={days} setDays={setDays} />
          </div>
          {crypto1Data?.id ? (
            <div className="grey-wrapper">
              <ListComponent coin={crypto1Data} />
            </div>
          ) : null}
          {crypto2Data?.id ? (
            <div className="grey-wrapper">
              <ListComponent coin={crypto2Data} />
            </div>
          ) : null}
          <div className="grey-wrapper chart-wrapper">
            <ToggleComponent priceType={priceType} setPriceType={setPriceType} />
            <LineChart priceType={priceType} chartData={dataSet} multiAxis={true} />
          </div>
          {crypto1Data?.id ? (
            <CoinInfo heading={crypto1Data.name} desc={crypto1Data.desc} />
          ) : null}
          {crypto2Data?.id ? (
            <CoinInfo heading={crypto2Data.name} desc={crypto2Data.desc} />
          ) : null}
        </div>
      )}
    </div>
  );
}
